<template>
  <div class="toolbar" :class="{ 'is-disabled': disabled }">
    <!-- Undo & Redo Group -->
    <div class="toolbar-group">
      <Tooltip :text="$t('editor.undo')" position="bottom">
        <button
          @click="editor.chain().focus().undo().run()"
          class="toolbar-btn"
        >
          <Undo class="icon" />
        </button>
      </Tooltip>
      <Tooltip :text="$t('editor.redo')" position="bottom">
        <button
          @click="editor.chain().focus().redo().run()"
          class="toolbar-btn"
        >
          <Redo class="icon" />
        </button>
      </Tooltip>
    </div>

    <div class="divider"></div>

    <!-- Basic Formatting Group -->
    <div class="toolbar-group">
      <Tooltip :text="$t('editor.bold')" position="bottom">
        <button
          @click="editor.chain().focus().toggleBold().run()"
          :class="{ 'is-active': editor.isActive('bold') }"
          class="toolbar-btn"
        >
          <Bold class="icon" />
        </button>
      </Tooltip>
      <Tooltip :text="$t('editor.italic')" position="bottom">
        <button
          @click="editor.chain().focus().toggleItalic().run()"
          :class="{ 'is-active': editor.isActive('italic') }"
          class="toolbar-btn"
        >
          <Italic class="icon" />
        </button>
      </Tooltip>
      <Tooltip :text="$t('editor.underline')" position="bottom">
        <button
          @click="editor.chain().focus().toggleUnderline().run()"
          :class="{ 'is-active': editor.isActive('underline') }"
          class="toolbar-btn"
        >
          <UnderlineIcon class="icon" />
        </button>
      </Tooltip>
      <Tooltip :text="$t('editor.strikethrough')" position="bottom">
        <button
          @click="editor.chain().focus().toggleStrike().run()"
          :class="{ 'is-active': editor.isActive('strike') }"
          class="toolbar-btn"
        >
          <Strikethrough class="icon" />
        </button>
      </Tooltip>
      <div class="relative highlight-dropdown">
        <Tooltip :text="$t('editor.highlight')" position="bottom">
          <button
            @click="showHighlightDropdown = !showHighlightDropdown"
            :class="{ 'is-active': editor.isActive('highlight') }"
            class="toolbar-btn"
          >
            <Highlighter class="icon" />
          </button>
        </Tooltip>
        <Transition name="fade">
          <div 
            v-if="showHighlightDropdown" 
            class="highlight-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg"
            @click.stop
          >
            <div class="color-grid">
              <button 
                v-for="color in highlightColors" 
                :key="color.value" 
                @click="setHighlightColor(color.value)"
                :style="{ backgroundColor: color.value }"
                class="color-btn"
                :title="$t(color.labelKey)"
              ></button>
              <button 
                @click="clearHighlight"
                class="clear-highlight-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400"
                :title="$t('editor.clearHighlight')"
              >
                <XIcon class="clear-icon" />
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <div class="relative color-dropdown">
        <Tooltip :text="$t('editor.fontColor')" position="bottom">
          <button
            @click="showColorDropdown = !showColorDropdown"
            :class="{ 'is-active': editor.isActive('fontColor') }"
            class="toolbar-btn"
          >
            <PaletteIcon class="icon" />
          </button>
        </Tooltip>
        <Transition name="fade">
          <div 
            v-if="showColorDropdown" 
            class="color-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg"
            @click.stop
          >
            <div class="color-grid">
              <button 
                v-for="color in fontColors" 
                :key="color.value" 
                @click="setFontColor(color.value)"
                :style="{ backgroundColor: color.value }"
                class="color-btn"
                :title="$t(color.labelKey)"
              ></button>
              <button 
                @click="clearFontColor"
                class="clear-highlight-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-gray-400"
                :title="$t('editor.none')"
              >
                <XIcon class="clear-icon" />
              </button>
            </div>
          </div>
        </Transition>
      </div>
      <div class="relative chord-dropdown">
        <Tooltip :text="$t('editor.insertChord')" position="bottom">
          <button
            @click="handleChordButtonClick"
            :class="{ 'is-active': editor.isActive('chord') || showChordDropdown }"
            class="toolbar-btn"
          >
            <MusicIcon class="icon" />
          </button>
        </Tooltip>
        <Transition name="fade">
          <div 
            v-if="showChordDropdown" 
            class="chord-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-3"
            @click.stop
          >
            <!-- Roots Selector -->
            <div class="chord-section">
              <span class="chord-section-title">{{ $t('editor.tone') }}:</span>
              <div class="chord-grid-roots">
                <button 
                  v-for="root in roots" 
                  :key="root" 
                  @click="selectedRoot = root"
                  :class="{ 'is-selected': selectedRoot === root }"
                  class="chord-root-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200"
                >{{ root }}</button>
              </div>
            </div>

            <!-- Accidentals Selector -->
            <div class="chord-section mt-2">
              <span class="chord-section-title">{{ $t('editor.accidentals') }}:</span>
              <div class="chord-row-options">
                <button 
                  @click="selectedAccidental = ''"
                  :class="{ 'is-selected': selectedAccidental === '' }"
                  class="chord-option-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200"
                >{{ $t('editor.natural') }}</button>
                <button 
                  @click="selectedAccidental = '#'"
                  :class="{ 'is-selected': selectedAccidental === '#' }"
                  class="chord-option-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200"
                >#</button>
                <button 
                  @click="selectedAccidental = 'b'"
                  :class="{ 'is-selected': selectedAccidental === 'b' }"
                  class="chord-option-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200"
                >b</button>
              </div>
            </div>

            <!-- Tonality Selector -->
            <div class="chord-section mt-2">
              <span class="chord-section-title">{{ $t('editor.type') }}:</span>
              <div class="chord-row-options">
                <button 
                  @click="selectedType = ''"
                  :class="{ 'is-selected': selectedType === '' }"
                  class="chord-option-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200"
                >{{ $t('editor.major') }}</button>
                <button 
                  @click="selectedType = 'm'"
                  :class="{ 'is-selected': selectedType === 'm' }"
                  class="chord-option-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200"
                >{{ $t('editor.minor') }}</button>
              </div>
            </div>

            <!-- Extensions Selector -->
            <div class="chord-section mt-2">
              <span class="chord-section-title">{{ $t('editor.blockquote.note') }}:</span>
              <div class="chord-row-options">
                <button 
                  v-for="ext in extensions" 
                  :key="ext.value" 
                  @click="selectedExtension = ext.value"
                  :class="{ 'is-selected': selectedExtension === ext.value }"
                  class="chord-option-btn border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200"
                >{{ ext.labelKey ? $t(ext.labelKey) : ext.label }}</button>
              </div>
            </div>

            <!-- Action Block -->
            <div class="chord-actions mt-3 border-t border-gray-100 dark:border-slate-700 pt-2 flex items-center justify-between">
              <span class="chord-preview font-bold text-lg text-indigo-600 dark:text-indigo-400">{{ computedChord }}</span>
              <button 
                @click="insertSelectedChord"
                class="chord-insert-btn bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 rounded text-sm font-semibold transition-colors"
              >{{ $t('editor.insert') }}</button>
            </div>
          </div>
        </Transition>
      </div>

      <!-- MusicSheet & Tablature Dropdown (Next to Insert Chord) -->
      <div class="relative music-sheet-dropdown" v-if="canInsertMusicSheet">
        <Tooltip :text="$t('editor.insertMusicSheet')" position="bottom">
          <button
            @click="showMusicSheetDropdown = !showMusicSheetDropdown"
            :class="{ 'is-active': editor.isActive('musicSheet') || showMusicSheetDropdown }"
            class="toolbar-btn"
          >
            <!-- Staff / Pentagram with notes Icon (Distinct from single chord note) -->
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <!-- Staff / Sheet lines -->
              <line x1="2" y1="5" x2="22" y2="5" stroke-width="1.2" opacity="0.65" />
              <line x1="2" y1="9" x2="22" y2="9" stroke-width="1.2" opacity="0.65" />
              <line x1="2" y1="13" x2="22" y2="13" stroke-width="1.2" opacity="0.65" />
              <line x1="2" y1="17" x2="22" y2="17" stroke-width="1.2" opacity="0.65" />
              <line x1="2" y1="21" x2="22" y2="21" stroke-width="1.2" opacity="0.65" />
              <!-- Musical notes on staff -->
              <path d="M8 17V7l7-2v10" fill="none" stroke-width="2" />
              <circle cx="6" cy="17" r="2.2" fill="currentColor" stroke="none" />
              <circle cx="13" cy="15" r="2.2" fill="currentColor" stroke="none" />
            </svg>
          </button>
        </Tooltip>
        <Transition name="fade">
          <div 
            v-if="showMusicSheetDropdown" 
            class="music-sheet-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-2"
            @click.stop
          >
            <div class="music-sheet-list flex flex-col gap-1 min-w-[210px]">
              <!-- Standard Music Sheet -->
              <button 
                @click="insertMusicSheetOption({ isTab: false })"
                class="flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md transition-colors text-left"
              >
                <div class="p-1 rounded bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
                  </svg>
                </div>
                <div>
                  <div class="font-semibold">{{ $t('editor.insertMusicSheetStandard') }}</div>
                  <div class="text-[10px] text-gray-400">Pauta pentagrama tradicional</div>
                </div>
              </button>

              <!-- Music Sheet + Tab -->
              <button 
                @click="insertMusicSheetOption({ isTab: true, tabOnly: false })"
                class="flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md transition-colors text-left"
              >
                <div class="p-1 rounded bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="3" y1="6" x2="21" y2="6" stroke-width="1.5" />
                    <line x1="3" y1="10" x2="21" y2="10" stroke-width="1.5" />
                    <line x1="3" y1="14" x2="21" y2="14" stroke-width="1.5" />
                    <line x1="3" y1="18" x2="21" y2="18" stroke-width="1.5" />
                  </svg>
                </div>
                <div>
                  <div class="font-semibold">{{ $t('editor.insertTabWithStaff') }}</div>
                  <div class="text-[10px] text-gray-400">Notas na pauta + Tablatura</div>
                </div>
              </button>

              <!-- Tab Only -->
              <button 
                @click="insertMusicSheetOption({ isTab: true, tabOnly: true })"
                class="flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md transition-colors text-left"
              >
                <div class="p-1 rounded bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400">
                  <span class="text-[10px] font-black px-0.5 tracking-tighter">TAB</span>
                </div>
                <div>
                  <div class="font-semibold">{{ $t('editor.insertTabOnly') }}</div>
                  <div class="text-[10px] text-gray-400">6 cordas de violão / guitarra</div>
                </div>
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>
    
    <div class="divider"></div>
    
    <!-- Font Size Dropdown -->
    <div class="toolbar-group">
      <FontSizeDropdown :editor="editor" />
    </div>

    <div class="divider"></div>

    <!-- Alignment Dropdown -->
    <div class="toolbar-group relative align-dropdown">
      <Tooltip :text="$t('editor.alignment')" position="bottom">
        <button
          @click="showAlignDropdown = !showAlignDropdown"
          :class="{ 'is-active': showAlignDropdown }"
          class="toolbar-btn"
        >
          <component :is="activeAlignIcon" class="icon" />
        </button>
      </Tooltip>
      <Transition name="fade">
        <div 
          v-if="showAlignDropdown" 
          class="align-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-2"
          @click.stop
        >
          <div class="align-list">
            <button 
              @click="setAlign('left')"
              :class="{ 'is-selected': editor.isActive({ textAlign: 'left' }) }"
              class="align-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              :title="$t('editor.alignLeft')"
            >
              <AlignLeft class="w-4 h-4 mr-2" />
              {{ $t('editor.alignLeft') }}
            </button>
            <button 
              @click="setAlign('center')"
              :class="{ 'is-selected': editor.isActive({ textAlign: 'center' }) }"
              class="align-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              :title="$t('editor.alignCenter')"
            >
              <AlignCenter class="w-4 h-4 mr-2" />
              {{ $t('editor.alignCenter') }}
            </button>
            <button 
              @click="setAlign('right')"
              :class="{ 'is-selected': editor.isActive({ textAlign: 'right' }) }"
              class="align-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              :title="$t('editor.alignRight')"
            >
              <AlignRight class="w-4 h-4 mr-2" />
              {{ $t('editor.alignRight') }}
            </button>
            <button 
              @click="setAlign('justify')"
              :class="{ 'is-selected': editor.isActive({ textAlign: 'justify' }) }"
              class="align-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              :title="$t('editor.alignJustify')"
            >
              <AlignJustify class="w-4 h-4 mr-2" />
              {{ $t('editor.alignJustify') }}
            </button>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Case Dropdown -->
    <div class="toolbar-group relative case-dropdown">
      <Tooltip :text="$t('editor.textCase')" position="bottom">
        <button
          @click="showCaseDropdown = !showCaseDropdown"
          :class="{ 'is-active': showCaseDropdown }"
          class="toolbar-btn"
        >
          <CaseSensitive class="icon" />
        </button>
      </Tooltip>
      <Transition name="fade">
        <div 
          v-if="showCaseDropdown" 
          class="case-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-2"
          @click.stop
        >
          <div class="case-list">
            <button 
              @click="setTextCaseFormat('upper')"
              class="case-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 text-xs font-semibold"
            >
              {{ $t('editor.uppercase') }}
            </button>
            <button 
              @click="setTextCaseFormat('lower')"
              class="case-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 text-xs font-semibold"
            >
              {{ $t('editor.lowercase') }}
            </button>
            <button 
              @click="setTextCaseFormat('capitalize')"
              class="case-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 text-xs font-semibold"
            >
              {{ $t('editor.capitalize') }}
            </button>
          </div>
        </div>
      </Transition>
    </div>

    <div class="divider"></div>

    <!-- Quotes & Media Group -->
    <div class="toolbar-group">
      <div class="relative blockquote-dropdown">
        <Tooltip :text="$t('editor.sections')" position="bottom">
          <button
            @click="showBlockquoteDropdown = !showBlockquoteDropdown"
            :class="{ 'is-active': editor.isActive('customBlockquote') || showBlockquoteDropdown }"
            class="toolbar-btn"
          >
            <Quote class="icon" />
          </button>
        </Tooltip>
        <Transition name="fade">
          <div 
            v-if="showBlockquoteDropdown" 
            class="blockquote-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-2"
            @click.stop
          >
            <div class="blockquote-list">
              <button 
                v-for="bType in blockquoteTypes" 
                :key="bType.value" 
                @click="toggleBlockquoteType(bType.value)"
                :class="{ 'is-selected': editor.isActive('customBlockquote', { type: bType.value }) }"
                class="blockquote-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <span class="color-dot" :style="{ backgroundColor: bType.color }"></span>
                {{ $t(bType.labelKey) }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
      <Tooltip :text="$t('editor.comment')" position="bottom">
        <button
          @click="addComment"
          :class="{ 'is-active': editor.isActive('comment') }"
          class="toolbar-btn"
          :disabled="editor.state.selection.empty"
        >
          <MessageSquare class="icon" />
        </button>
      </Tooltip>
      
      <!-- Vocal Highlight Dropdown -->
      <div class="relative vocal-dropdown">
        <Tooltip :text="$t('editor.vocals')" position="bottom">
          <button
            @click="showVocalDropdown = !showVocalDropdown"
            :class="{ 'is-active': editor.isActive('vocalClass') || showVocalDropdown }"
            class="toolbar-btn"
          >
            <Speech class="icon" />
          </button>
        </Tooltip>
        <Transition name="fade">
          <div 
            v-if="showVocalDropdown" 
            class="vocal-popover bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-lg p-2"
            @click.stop
          >
            <div class="vocal-list">
              <button 
                v-for="vocal in vocalOptions" 
                :key="vocal.value" 
                @click="setVocalHighlight(vocal.value)"
                :class="{ 'is-selected': editor.isActive('vocalClass', { class: vocal.value }) }"
                class="vocal-option-btn text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <span class="color-dot" :style="{ backgroundColor: vocal.color }"></span>
                {{ $t('editor.vocal', { num: vocal.value.replace('vocal', '') }) }}
              </button>
              <div class="divider my-1"></div>
              <button 
                @click="clearVocalHighlight"
                class="vocal-option-btn text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
              >
                <XIcon class="w-4 h-4 mr-2" />
                {{ $t('editor.clearHighlight') }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </div>
    
    <div class="divider" v-if="showTranspose"></div>

    <!-- Transpose Control -->
    <div class="toolbar-group" v-if="showTranspose">
      <EditorTranspose 
        :modelValue="semitone" 
        :disabled="disabled" 
        @change="onTransposeChange" 
      />
    </div>

    <!-- AI Auto-Edit (only for users with AI feature access) -->
    <div class="toolbar-group" v-if="hasAiFeature">
      <div class="divider"></div>
      <Tooltip :text="$t('editor.aiAutoEditTooltip')" position="bottom">
        <button
          @click="runAiEdit"
          :disabled="isAiEditing"
          class="toolbar-btn ai-edit-btn"
          :class="{ 'is-loading': isAiEditing }"
          :title="$t('editor.aiAutoEdit')"
        >
          <span v-if="isAiEditing" class="ai-spinner"></span>
          <Wand2 v-else class="icon ai-wand-icon" />
        </button>
      </Tooltip>
    </div>

  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, onUnmounted, computed } from 'vue';
import FontSizeDropdown from '@/components/common/elements/FontSizeDropdown.vue';
import EditorTranspose from './EditorTranspose.vue';
import Tooltip from '@/components/common/elements/TriadTooltip.vue';
import { useI18n } from 'vue-i18n';
import userStore from '@/store/user.store';
import toastStore from '@/store/toast.store';

const { t } = useI18n();
const emit = defineEmits(['comment-added', 'update:semitone', 'transpose']);

// Lucide Icons
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Highlighter,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
  Undo,
  Redo,
  Music as MusicIcon,
  X as XIcon,
  Palette as PaletteIcon,
  MessageSquare,
  Speech,
  CaseSensitive,
  Wand2
} from '@lucide/vue';

const props = defineProps({
  editor: {
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  semitone: {
    type: Number,
    default: 0
  },
  showTranspose: {
    type: Boolean,
    default: true
  }
});

const onTransposeChange = (event) => {
  emit('update:semitone', event.semitone);
  emit('transpose', event);
};

const showHighlightDropdown = ref(false);
const showColorDropdown = ref(false);
const showChordDropdown = ref(false);
const showBlockquoteDropdown = ref(false);
const showVocalDropdown = ref(false);
const showAlignDropdown = ref(false);
const showCaseDropdown = ref(false);
const showMusicSheetDropdown = ref(false);

const vocalOptions = [
  { label: 'Voz 1', value: 'vocal1', color: 'rgba(155, 158, 232, 0.45)' },
  { label: 'Voz 2', value: 'vocal2', color: 'rgba(231, 187, 187, 0.45)' },
  { label: 'Voz 3', value: 'vocal3', color: 'rgba(126, 212, 202, 0.45)' },
  { label: 'Voz 4', value: 'vocal4', color: 'rgba(239, 168, 106, 0.45)' },
  { label: 'Voz 5', value: 'vocal5', color: 'rgba(126, 212, 202, 0.45)' },
  { label: 'Voz 6', value: 'vocal6', color: 'rgba(160, 198, 120, 0.45)' },
  { label: 'Voz 7', value: 'vocal7', color: 'rgba(198, 212, 126, 0.45)' },
  { label: 'Voz 8', value: 'vocal8', color: 'rgba(126, 212, 202, 0.45)' },
  { label: 'Voz 9', value: 'vocal9', color: 'rgba(155, 126, 212, 0.45)' },
  { label: 'Voz 10', value: 'vocal10', color: 'rgba(212, 126, 196, 0.45)' }
];

const selectedRoot = ref('C');
const selectedAccidental = ref('');
const selectedType = ref('');
const selectedExtension = ref('');

const roots = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const extensions = [
  { labelKey: 'editor.none', value: '' },
  { label: '7', value: '7' },
  { label: '7+', value: '7+' },
  { label: 'sus4', value: 'sus4' }
];

const computedChord = computed(() => {
  return selectedRoot.value + selectedAccidental.value + selectedType.value + selectedExtension.value;
});

const activeAlignIcon = computed(() => {
  if (!props.editor) return AlignLeft;
  if (props.editor.isActive({ textAlign: 'center' })) return AlignCenter;
  if (props.editor.isActive({ textAlign: 'right' })) return AlignRight;
  if (props.editor.isActive({ textAlign: 'justify' })) return AlignJustify;
  return AlignLeft;
});

const blockquoteTypes = [
  { labelKey: 'editor.blockquote.chorus', value: 'chorus', color: '#3b82f6' },
  { labelKey: 'editor.blockquote.verse', value: 'verse', color: '#10b981' },
  { labelKey: 'editor.blockquote.intro', value: 'intro', color: '#6366f1' },
  { labelKey: 'editor.blockquote.arrange', value: 'arrange', color: '#6366f1' },
  { labelKey: 'editor.blockquote.bridge', value: 'bridge', color: '#f59e0b' },
  { labelKey: 'editor.blockquote.prelude', value: 'prelude', color: '#8b5cf6' },
  { labelKey: 'editor.blockquote.solo', value: 'solo', color: '#ec4899' },
  { labelKey: 'editor.blockquote.note', value: 'note', color: '#64748b' },
  { labelKey: 'editor.blockquote.finishing', value: 'finishing', color: '#ef4444' }
];

const highlightColors = [
  { labelKey: 'editor.colors.yellow', value: '#fef08a' },
  { labelKey: 'editor.colors.green', value: '#bbf7d0' },
  { labelKey: 'editor.colors.darkGreen', value: '#86efac' },
  { labelKey: 'editor.colors.blue', value: '#bfdbfe' },
  { labelKey: 'editor.colors.pink', value: '#fbcfe8' },
  { labelKey: 'editor.colors.purple', value: '#e9d5ff' },
  { labelKey: 'editor.colors.orange', value: '#fed7aa' }
];

const fontColors = [
  { labelKey: 'editor.colors.black', value: '#1e293b' },
  { labelKey: 'editor.colors.gray', value: '#64748b' },
  { labelKey: 'editor.colors.red', value: '#ef4444' },
  { labelKey: 'editor.colors.blue', value: '#3b82f6' },
  { labelKey: 'editor.colors.green', value: '#10b981' },
  { labelKey: 'editor.colors.purple', value: '#8b5cf6' },
  { labelKey: 'editor.colors.orange', value: '#f97316' }
];

const setHighlightColor = (color) => {
  if (!props.editor) return;
  props.editor.chain().focus().setHighlight({ color }).run();
  showHighlightDropdown.value = false;
};

const clearHighlight = () => {
  if (!props.editor) return;
  props.editor.chain().focus().unsetHighlight().run();
  showHighlightDropdown.value = false;
};

const setFontColor = (color) => {
  if (!props.editor) return;
  props.editor.chain().focus().setColor(color).run();
  showColorDropdown.value = false;
};

const clearFontColor = () => {
  if (!props.editor) return;
  props.editor.chain().focus().unsetColor().run();
  showColorDropdown.value = false;
};

const handleChordButtonClick = () => {
  if (!props.editor) return;
  if (!props.editor.state.selection.empty) {
    const { from, to } = props.editor.state.selection;
    let isChordSelected = false;
    let selectedChordNode = null;
    props.editor.state.doc.nodesBetween(from, to, (node) => {
      if (node.type.name === 'chord') {
        isChordSelected = true;
        selectedChordNode = node;
      }
    });

    if (isChordSelected && selectedChordNode) {
      // Convert chord node back to plain text
      props.editor.chain().focus().insertContent(selectedChordNode.attrs.chord).run();
    } else {
      // Convert plain text to chord nodes
      const selectedText = props.editor.state.doc.textBetween(from, to, ' ');
      const parts = selectedText.split(/\s+/).filter(p => p.length > 0);
      if (parts.length >= 1) {
        const htmlParts = parts.map(part => {
          return `<sup data-type="chord" class="chord-sup"><span class="chord-span" draggable="true" contenteditable="false">${part}</span></sup>`;
        });
        const replacementHtml = htmlParts.join(' ');
        props.editor.chain().focus().insertContent(replacementHtml).run();
      }
    }
  } else {
    showChordDropdown.value = !showChordDropdown.value;
  }
};

const insertSelectedChord = () => {
  if (!props.editor) return;
  const chordName = computedChord.value;
  const isInlineSection = props.editor.isActive('customBlockquote', { type: 'intro' }) ||
                          props.editor.isActive('customBlockquote', { type: 'arrange' }) ||
                          props.editor.isActive('customBlockquote', { type: 'solo' }) ||
                          props.editor.isActive('customBlockquote', { type: 'finishing' });
                          
  const suffix = isInlineSection ? ' ' : '';
  props.editor.chain().focus().insertContent(`<sup data-type="chord" class="chord-sup"><span class="chord-span" draggable="true" contenteditable="false">${chordName}</span></sup>${suffix}`).run();
  showChordDropdown.value = false;
};

const toggleBlockquoteType = (type) => {
  if (!props.editor) return;
  props.editor.chain().focus().toggleCustomBlockquote(type).run();
  showBlockquoteDropdown.value = false;
};

const addComment = () => {
  if (!props.editor) return;
  if (props.editor.state.selection.empty) {
    return;
  }
  const commentText = prompt(t('editor.writeComment'));
  if (commentText && commentText.trim().length > 0) {
    props.editor.chain().focus().setComment(commentText.trim()).run();
    emit('comment-added');
  }
};

// MusicSheet block is optional — SongEditor registers it, other editors may not.
const canInsertMusicSheet = computed(() => {
  if (!props.editor) return false;
  try {
    return props.editor.can().insertMusicSheet({});
  } catch (e) {
    return false;
  }
});

const insertMusicSheetOption = (options = {}) => {
  if (!props.editor || !canInsertMusicSheet.value) return;
  props.editor.chain().focus().insertMusicSheet(options).run();
  showMusicSheetDropdown.value = false;
};

const handleClickOutsideDropdowns = (event) => {
  if (!event.target.closest('.highlight-dropdown')) {
    showHighlightDropdown.value = false;
  }
  if (!event.target.closest('.color-dropdown')) {
    showColorDropdown.value = false;
  }
  if (!event.target.closest('.chord-dropdown')) {
    showChordDropdown.value = false;
  }
  if (!event.target.closest('.blockquote-dropdown')) {
    showBlockquoteDropdown.value = false;
  }
  if (!event.target.closest('.vocal-dropdown')) {
    showVocalDropdown.value = false;
  }
  if (!event.target.closest('.align-dropdown')) {
    showAlignDropdown.value = false;
  }
  if (!event.target.closest('.case-dropdown')) {
    showCaseDropdown.value = false;
  }
  if (!event.target.closest('.music-sheet-dropdown')) {
    showMusicSheetDropdown.value = false;
  }
};

const setAlign = (alignment) => {
  if (!props.editor) return;
  props.editor.chain().focus().setTextAlign(alignment).run();
  showAlignDropdown.value = false;
};

const setTextCaseFormat = (caseType) => {
  if (!props.editor) return;
  props.editor.chain().focus().setTextCase(caseType).run();
  showCaseDropdown.value = false;
};

const setVocalHighlight = (vocalClass) => {
  if (!props.editor) return;
  props.editor.chain().focus().setVocalClass(vocalClass).run();
  showVocalDropdown.value = false;
};

const clearVocalHighlight = () => {
  if (!props.editor) return;
  props.editor.chain().focus().unsetVocalClass().run();
  showVocalDropdown.value = false;
};

onMounted(() => {
  document.addEventListener('click', handleClickOutsideDropdowns);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutsideDropdowns);
});

const hasAiFeature = computed(() => userStore.getters.hasAiFeature);
const isAiEditing = ref(false);

const runAiEdit = async () => {
  if (!props.editor || isAiEditing.value) return;

  const html = props.editor.getHTML();
  if (!html || html.trim() === '<p></p>') {
    toastStore.dispatch('addMessage', {
      message: 'Nothing to edit. Add some content first.',
      icon: 'info',
      ttl: 3000
    });
    return;
  }

  isAiEditing.value = true;
  try {
    const response = await fetch('/api/ai/edit-song', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html })
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `Server error ${response.status}`);
    }

    const data = await response.json();
    if (data.html) {
      props.editor.commands.setContent(data.html, true);
      toastStore.dispatch('addMessage', {
        message: 'Song edited successfully by AI!',
        icon: 'success',
        ttl: 3500
      });
    }
  } catch (err) {
    console.error('[EditorToolbar] AI auto-edit error:', err);
    toastStore.dispatch('addMessage', {
      message: err.message || 'AI editing failed. Please try again.',
      icon: 'error',
      ttl: 4000
    });
  } finally {
    isAiEditing.value = false;
  }
};





// Image tool removed
</script>

<style scoped>
/* Vocal Dropdown Styles */
.vocal-popover {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  border-radius: 8px;
  min-width: 170px;
  z-index: 60;
}

.vocal-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.vocal-option-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 12px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
}

.vocal-option-btn.is-selected {
  background-color: #f1f5f9;
}

html.dark .vocal-option-btn.is-selected {
  background-color: #334155;
}

/* Align Dropdown Styles */
.align-popover {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  border-radius: 8px;
  min-width: 140px;
  z-index: 60;
}

.align-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.align-option-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 12px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
}

.align-option-btn.is-selected {
  background-color: #f1f5f9;
}

html.dark .align-option-btn.is-selected {
  background-color: #334155;
}

/* Case Dropdown Styles */
.case-popover {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  border-radius: 8px;
  min-width: 170px;
  z-index: 60;
}

.case-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.case-option-btn {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
}

.case-option-btn.is-selected {
  background-color: #f1f5f9;
}

html.dark .case-option-btn.is-selected {
  background-color: #334155;
}

/* Toolbar Style */
.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 12px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  user-select: none;
  position: sticky;
  top: var(--editor-toolbar-top, 0px);
  z-index: 25;
}

.toolbar.is-disabled {
  pointer-events: none;
  opacity: 0.55;
}

html.dark .toolbar {
  background-color: #1e293b;
  border-bottom-color: #334155;
}

.toolbar-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Dropdown heading-select */
.heading-select {
  height: 32px;
  padding: 0 8px;
  border: 1px solid #cbd5e1;
  background-color: #ffffff;
  color: #334155;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

html.dark .heading-select {
  border-color: #475569;
  background-color: #0f172a;
  color: #cbd5e1;
}

.heading-select:hover {
  border-color: #94a3b8;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  background: transparent;
  color: #64748b;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

html.dark .toolbar-btn {
  color: #94a3b8;
}

.toolbar-btn:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

html.dark .toolbar-btn:hover {
  background-color: #334155;
  color: #f8fafc;
}

.toolbar-btn.is-active {
  background-color: #e2e8f0;
  color: #0f172a;
  border-color: #cbd5e1;
}

html.dark .toolbar-btn.is-active {
  background-color: #475569;
  color: #f8fafc;
  border-color: #64748b;
}

.icon {
  width: 16px;
  height: 16px;
}

.divider {
  align-self: stretch;
  width: 1px;
  background-color: #e2e8f0;
  margin: 4px 6px;
}

html.dark .divider {
  background-color: #334155;
}

.ml-auto {
  margin-left: auto;
}

/* Float Color Selector Dropdown Styles */
.highlight-dropdown, .color-dropdown {
  position: relative;
}

.highlight-popover, .color-popover {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  border-radius: 8px;
  padding: 8px;
  z-index: 1000;
  min-width: 140px;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  align-items: center;
}

.color-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s ease-in-out;
}

.color-btn:hover {
  transform: scale(1.15);
}

.clear-highlight-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s ease-in-out;
}

.clear-highlight-btn:hover {
  transform: scale(1.15);
}

.clear-icon {
  width: 14px;
  height: 14px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Chord Popover Styles */
.chord-popover {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  border-radius: 8px;
  width: 250px;
  z-index: 1000;
}

.chord-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chord-section-title {
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.05em;
}

.chord-grid-roots {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.chord-root-btn {
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;
}

.chord-row-options {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chord-option-btn {
  flex: 1;
  min-width: 48px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px 6px;
  transition: all 0.15s ease;
}

.chord-root-btn.is-selected,
.chord-option-btn.is-selected {
  background-color: #6366f1;
  color: #ffffff;
  border-color: #6366f1;
}

.chord-insert-btn {
  cursor: pointer;
}

/* Custom Blockquote Dropdown Styles */
.blockquote-dropdown,
.music-sheet-dropdown {
  position: relative;
}

.blockquote-popover,
.music-sheet-popover {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  border-radius: 8px;
  z-index: 10000;
}

.blockquote-popover {
  width: 180px;
}

.blockquote-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.blockquote-option-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  border: none;
  background-color: transparent;
  transition: all 0.15s ease;
}

.blockquote-option-btn.is-selected {
  background-color: #6366f1;
  color: #ffffff !important;
}

.color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* AI Auto-Edit Button */
.ai-edit-btn {
  width: auto !important;
  padding: 0 10px;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
  border-color: #ddd6fe;
  background: linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%);
  transition: all 0.2s ease;
}

.ai-edit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ddd6fe 0%, #ede9fe 100%);
  color: #6d28d9;
  border-color: #c4b5fd;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
}

html.dark .ai-edit-btn {
  color: #a78bfa;
  border-color: #4c1d95;
  background: linear-gradient(135deg, #2e1065 0%, #3b0764 100%);
}

html.dark .ai-edit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #3b0764 0%, #4c1d95 100%);
  color: #c4b5fd;
  border-color: #6d28d9;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.25);
}

.ai-edit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.ai-wand-icon {
  color: #7c3aed;
  animation: wand-idle 3s ease-in-out infinite;
}

html.dark .ai-wand-icon {
  color: #a78bfa;
}

@keyframes wand-idle {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-8deg); }
  75% { transform: rotate(8deg); }
}

.ai-edit-btn.is-loading .ai-wand-icon,
.ai-edit-btn:hover .ai-wand-icon {
  animation: none;
}

/* Spinner for AI loading state */
.ai-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #c4b5fd;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: ai-spin 0.7s linear infinite;
  flex-shrink: 0;
}

html.dark .ai-spinner {
  border-color: #4c1d95;
  border-top-color: #a78bfa;
}

@keyframes ai-spin {
  to { transform: rotate(360deg); }
}
</style>
